// src/components/ParentDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Spinner,
  Center,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useMediaQuery,
  Flex,
  Icon,
  Heading,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Avatar,
  useColorModeValue,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
} from '@chakra-ui/react';
import { 
  FiFileText, 
  FiCalendar, 
  FiDownload, 
  FiMessageCircle,
  FiUser,
  FiActivity,
  FiTrendingUp,
  FiClock,
  FiChevronDown,
  FiUsers
} from 'react-icons/fi';
import { InfoIcon } from '@chakra-ui/icons';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabaseClient';
import { getClientDisplayAge } from '../utils/clientAge';
import {
  formatAttendanceStatusLabel,
  getAttendanceStatusColorScheme,
  isCancelledOrLateCancellationStatus,
  formatMakeupBadgeDate,
} from '../utils/attendanceDisplay';

const LoadingOverlay = () => (
  <Center height="100%" width="100%">
    <VStack spacing={4}>
      <Spinner 
        thickness="4px"
        speed="0.65s"
        emptyColor="gray.200"
        color="#24b5e8"
        size="xl"
      />
      <Text color="gray.500">Loading your child's information...</Text>
    </VStack>
  </Center>
);

const BottomNavItem = ({ icon, label, isSelected, onClick }) => (
  <VStack
    as="button"
    spacing={1}
    flex={1}
    py={2}
    px={4}
    onClick={onClick}
    color={isSelected ? "#24b5e8" : "gray.600"}
    bg={isSelected ? "blue.50" : "transparent"}
    transition="all 0.2s"
    _hover={{
      color: "#24b5e8",
      bg: "blue.50",
    }}
  >
    <Icon as={icon} boxSize={5} />
    <Text fontSize="xs" fontWeight={isSelected ? "medium" : "normal"}>
      {label}
    </Text>
  </VStack>
);

// Document Trail Component
const DocumentTrail = ({ childId }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [groupedDocs, setGroupedDocs] = useState({});
  const toast = useToast();

  useEffect(() => {
    fetchDocuments();
  }, [childId]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', childId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      
      setDocuments(data || []);
      
      // Group documents by category
      const grouped = (data || []).reduce((groups, doc) => {
        const category = doc.document_category;
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(doc);
        return groups;
      }, {});
      
      setGroupedDocs(grouped);
    } catch (error) {
      toast({
        title: 'Error loading documents',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const { data, error } = await supabase.storage
        .from('client-documents')
        .createSignedUrl(doc.file_path, 60);

      if (error) throw error;

      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'Download started',
        description: `Downloading ${doc.file_name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) return <LoadingOverlay />;

  return (
    <VStack spacing={6} align="stretch">
      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <Stat bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.100">
          <StatLabel color="blue.600">Total Documents</StatLabel>
          <StatNumber color="blue.800">{documents.length}</StatNumber>
        </Stat>
        <Stat bg="green.50" p={4} borderRadius="lg" border="1px solid" borderColor="green.100">
          <StatLabel color="green.600">Session Notes</StatLabel>
          <StatNumber color="green.800">
            {groupedDocs['Session Notes']?.length || 0}
          </StatNumber>
        </Stat>
        <Stat bg="purple.50" p={4} borderRadius="lg" border="1px solid" borderColor="purple.100">
          <StatLabel color="purple.600">Progress Reports</StatLabel>
          <StatNumber color="purple.800">
            {groupedDocs['Progress Reports']?.length || 0}
          </StatNumber>
        </Stat>
        <Stat bg="orange.50" p={4} borderRadius="lg" border="1px solid" borderColor="orange.100">
          <StatLabel color="orange.600">Categories</StatLabel>
          <StatNumber color="orange.800">
            {Object.keys(groupedDocs).length}
          </StatNumber>
        </Stat>
      </SimpleGrid>

      {/* Documents by Category */}
      <VStack spacing={6} align="stretch">
        {Object.entries(groupedDocs).map(([category, categoryDocs]) => (
          <Card key={category} variant="outline">
            <CardHeader>
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FiFileText} color="#24b5e8" />
                  <Heading size="md">{category}</Heading>
                </HStack>
                <Badge colorScheme="blue">{categoryDocs.length} files</Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="stretch">
                {categoryDocs.map(doc => (
                  <HStack 
                    key={doc.id} 
                    p={4} 
                    borderWidth="1px" 
                    borderRadius="lg" 
                    bg="gray.50"
                    _hover={{ bg: "gray.100" }}
                    transition="all 0.2s"
                  >
                    <Icon as={FiFileText} color="gray.500" />
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontWeight="medium">{doc.title}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {doc.document_type} • {formatFileSize(doc.file_size)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {new Date(doc.uploaded_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Text>
                    </VStack>
                    <Button
                      leftIcon={<FiDownload />}
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => downloadDocument(doc)}
                    >
                      Download
                    </Button>
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        ))}
        
        {documents.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>
              No documents available yet. Documents will appear here as your child progresses through therapy.
            </AlertDescription>
          </Alert>
        )}
      </VStack>
    </VStack>
  );
};

// Session Log Component
const SessionLog = ({ childId }) => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    fetchSessions();
  }, [childId]);

  const fetchSessions = async () => {
    try {
      // First, get the attendance logs
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('session_attendance')
        .select(`
          *,
          session:sessions (
            day,
            time,
            session_type,
            is_makeup,
            makeup_for,
            therapist:therapists (
              name,
              specialization
            )
          )
        `)
        .eq('client_id', childId)
        .order('attendance_date', { ascending: false });

      if (attendanceError) throw attendanceError;

      const linkedMakeupAttendanceIds = attendanceData
        .filter(
          (session) =>
            isCancelledOrLateCancellationStatus(session.status) &&
            (session.madeup_for === true || session.makeup_scheduled === true)
        )
        .map((session) => session.id);

      let makeupSessionsData = [];
      if (linkedMakeupAttendanceIds.length > 0) {
        console.log('Looking for makeup sessions for attendance IDs:', linkedMakeupAttendanceIds);

        const { data: makeupData, error: makeupError } = await supabase
          .from('sessions')
          .select('session_attendance_id, valid_from, id')
          .in('session_attendance_id', linkedMakeupAttendanceIds);

        if (makeupError) throw makeupError;
        makeupSessionsData = makeupData || [];
        console.log('Found makeup sessions:', makeupSessionsData);
      }

      // Combine the data
      const sessionsWithMakeupDates = attendanceData.map(session => {
        const makeupSession = makeupSessionsData.find(
          makeup => String(makeup.session_attendance_id) === String(session.id)
        );
        
        if (
          session.madeup_for === true &&
          isCancelledOrLateCancellationStatus(session.status)
        ) {
          console.log(
            `For cancelled or late-cancelled session ${session.id} (${session.attendance_date}):`,
            {
            foundMakeupSession: !!makeupSession,
            makeupDate: makeupSession?.valid_from,
            originalDate: session.attendance_date
          });
        }
        
        return {
          ...session,
          makeup_session_date: makeupSession?.valid_from
        };
      });

      setSessions(sessionsWithMakeupDates || []);
    } catch (error) {
      toast({
        title: 'Error loading sessions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const hour = parseInt(time.split(':')[0]);
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:00 ${ampm}`;
  };

  const getStats = () => {
    const total = sessions.length;
    const present = sessions.filter(s => s.status === 'present').length;
    const cancelled = sessions.filter(s => s.status === 'cancelled').length;
    
    // Count sessions that require makeup (madeup_for is not null - either true or false)
    const requiredForMakeup = sessions.filter(s => s.madeup_for !== null).length;
    
    // Count sessions that have been made up (madeup_for is true)
    const madeUp = sessions.filter(s => s.madeup_for === true).length;
    
    // Count makeup sessions (sessions created to make up for others)
    const makeupSessions = sessions.filter(s => s.session?.is_makeup === true).length;
    
    // Actual sessions = total minus makeup sessions
    const actualSessions = total - makeupSessions;
    
    return { total, present, cancelled, requiredForMakeup, madeUp, actualSessions };
  };

  const stats = getStats();

  if (isLoading) return <LoadingOverlay />;

  return (
    <VStack spacing={6} align="stretch">
      {/* Session Summary */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <Stat bg="green.50" p={4} borderRadius="lg">
          <HStack spacing={1}>
            <StatLabel color="green.600">Total Sessions</StatLabel>
            <Tooltip 
              label="Total number of sessions including makeup sessions. Actual sessions are original scheduled sessions only."
              fontSize="sm"
            >
              <InfoIcon boxSize={3} color="gray.400" />
            </Tooltip>
          </HStack>
          <StatNumber color="green.800">
            <HStack>
              <Text fontSize="2xl">{stats.total}</Text>
              <Text as="span" fontSize="xs" color="black" fontWeight="semibold" mt={0}>
                ({stats.actualSessions} actual sessions)
              </Text>
            </HStack>
          </StatNumber>
        </Stat>
        <Stat bg="blue.50" p={4} borderRadius="lg">
          <StatLabel color="blue.600">Present</StatLabel>
          <StatNumber color="blue.800">{stats.present}</StatNumber>
        </Stat>
        <Stat bg="orange.50" p={4} borderRadius="lg">
          <HStack spacing={1}>
            <StatLabel color="orange.600">Cancelled</StatLabel>
            <Tooltip 
              label="Number in parentheses shows how many cancelled sessions require makeup sessions to be scheduled."
              fontSize="sm"
            >
              <InfoIcon boxSize={3} color="gray.400" />
            </Tooltip>
          </HStack>
          <StatNumber color="orange.500">
            <HStack>
              <Text fontSize="2xl" color="orange.500">{stats.cancelled}</Text>
              <Text as="span" fontSize="xs" color="black" fontWeight="semibold" mt={0}>
                ({stats.requiredForMakeup} required for makeup)
              </Text>
            </HStack>
          </StatNumber>
        </Stat>
        <Stat bg="purple.50" p={4} borderRadius="lg">
          <HStack spacing={1}>
            <StatLabel color="purple.600">Made up for</StatLabel>
            <Tooltip 
              label="Shows how many required makeup sessions have been successfully completed."
              fontSize="sm"
            >
              <InfoIcon boxSize={3} color="gray.400" />
            </Tooltip>
          </HStack>
          <StatNumber color="purple.800">
            {stats.madeUp} out of {stats.requiredForMakeup}
          </StatNumber>
        </Stat>
      </SimpleGrid>

      {/* Sessions Table */}
      <Card variant="outline">
        <CardHeader>
          <HStack>
            <Icon as={FiCalendar} color="#24b5e8" />
            <Heading size="md">Recent Sessions</Heading>
          </HStack>
        </CardHeader>
        <CardBody>
          {sessions.length > 0 ? (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Date & Time</Th>
                  <Th>Therapist</Th>
                  <Th display={{ base: "none", md: "table-cell" }}>Service</Th>
                  <Th display={{ base: "none", sm: "table-cell" }}>Status</Th>
                  <Th display={{ base: "none", lg: "table-cell" }}>Notes</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sessions.map(session => (
                  <Tr key={session.id}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" fontSize={{ base: "sm", md: "md" }}>
                          {new Date(session.attendance_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {session.session?.day} {formatTime(session.session?.time)}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontSize={{ base: "sm", md: "md" }}>{session.session?.therapist?.name}</Text>
                        <Text fontSize="xs" color="gray.600">
                          {session.session?.therapist?.specialization}
                        </Text>
                        {/* Show service type and status on mobile as small badges under therapist */}
                        <VStack align="start" spacing={1} mt={1} display={{ base: "flex", sm: "none" }}>
                          <HStack spacing={2}>
                            <Badge 
                              display={{ base: "inline-flex", md: "none" }} 
                              colorScheme="blue" 
                              size="sm"
                            >
                              {session.session?.session_type || 'Regular'}
                            </Badge>
                            <Badge 
                              colorScheme={getAttendanceStatusColorScheme(session.status)} 
                              size="sm"
                            >
                              {formatAttendanceStatusLabel(session.status)}
                            </Badge>
                          </HStack>
                          {session.session?.is_makeup && session.session?.makeup_for && (
                            <Badge colorScheme="cyan" variant="outline" size="xs">
                              {session.status === 'present'
                                ? `Makeup for ${formatMakeupBadgeDate(session.session.makeup_for)}`
                                : `Supposed Makeup for ${formatMakeupBadgeDate(session.session.makeup_for)}`}
                            </Badge>
                          )}
                          {session.voided_linked_makeup_on &&
                            isCancelledOrLateCancellationStatus(session.status) && (
                            <>
                              <Badge colorScheme="purple" variant="outline" size="xs">
                                Supposed Makeup on {formatMakeupBadgeDate(session.voided_linked_makeup_on)}{' '}
                                cancelled by therapist
                              </Badge>
                              <Badge colorScheme="green" variant="outline" size="xs">
                                Makeup no longer required
                              </Badge>
                            </>
                          )}
                          {session.makeup_fee_voided_by_therapist &&
                            session.session?.is_makeup &&
                            isCancelledOrLateCancellationStatus(session.status) &&
                            session.session?.makeup_for && (
                            <Badge colorScheme="purple" variant="outline" size="xs">
                              Makeup for {formatMakeupBadgeDate(session.session.makeup_for)} cancelled by therapist
                            </Badge>
                          )}
                          {isCancelledOrLateCancellationStatus(session.status) &&
                            session.makeup_scheduled === true &&
                            !session.voided_linked_makeup_on &&
                            session.madeup_for !== true &&
                            session.makeup_session_date && (
                            <Badge colorScheme="blue" variant="outline" size="xs">
                              Makeup scheduled on{' '}
                              {new Date(session.makeup_session_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </Badge>
                          )}
                          {session.madeup_for === false && (
                            <Badge colorScheme="red" variant="outline" size="xs">
                              Makeup Required
                            </Badge>
                          )}
                          {session.grace_cancellation && session.status === 'absent' && (
                            <Badge colorScheme="teal" variant="solid" size="xs">
                              Grace Cancellation
                            </Badge>
                          )}
                          {session.waived_late_cancellation && session.status === 'absent' && (
                            <Badge colorScheme="cyan" variant="solid" size="xs">
                              Waived Late Cancellation
                            </Badge>
                          )}
                          {session.madeup_for === true &&
                          isCancelledOrLateCancellationStatus(session.status) &&
                          session.makeup_session_date && (
                            <Badge colorScheme="green" variant="outline" size="xs">
                              Made up for on {new Date(session.makeup_session_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </Badge>
                          )}
                        </VStack>
                      </VStack>
                    </Td>
                    <Td display={{ base: "none", md: "table-cell" }}>
                      <Badge colorScheme="blue">
                        {session.session?.session_type || 'Regular'}
                      </Badge>
                    </Td>
                    <Td display={{ base: "none", sm: "table-cell" }}>
                      <VStack align="start" spacing={1}>
                        <Badge colorScheme={getAttendanceStatusColorScheme(session.status)} size={{ base: "sm", md: "md" }}>
                          {formatAttendanceStatusLabel(session.status)}
                        </Badge>
                        {session.session?.is_makeup && session.session?.makeup_for && (
                          <Badge colorScheme="cyan" variant="outline" size="sm">
                            {session.status === 'present'
                              ? `Makeup for ${formatMakeupBadgeDate(session.session.makeup_for)}`
                              : `Supposed Makeup for ${formatMakeupBadgeDate(session.session.makeup_for)}`}
                          </Badge>
                        )}
                        {session.voided_linked_makeup_on &&
                          isCancelledOrLateCancellationStatus(session.status) && (
                          <>
                            <Badge colorScheme="purple" variant="outline" size="sm">
                              Supposed Makeup on {formatMakeupBadgeDate(session.voided_linked_makeup_on)}{' '}
                              cancelled by therapist
                            </Badge>
                            <Badge colorScheme="green" variant="outline" size="sm">
                              Makeup no longer required
                            </Badge>
                          </>
                        )}
                        {session.makeup_fee_voided_by_therapist &&
                          session.session?.is_makeup &&
                          isCancelledOrLateCancellationStatus(session.status) &&
                          session.session?.makeup_for && (
                          <Badge colorScheme="purple" variant="outline" size="sm">
                            Makeup for {formatMakeupBadgeDate(session.session.makeup_for)} cancelled by therapist
                          </Badge>
                        )}
                        {isCancelledOrLateCancellationStatus(session.status) &&
                          session.makeup_scheduled === true &&
                          !session.voided_linked_makeup_on &&
                          session.madeup_for !== true &&
                          session.makeup_session_date && (
                          <Badge colorScheme="blue" variant="outline" size="sm">
                            Makeup scheduled on{' '}
                            {new Date(session.makeup_session_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Badge>
                        )}
                        {session.madeup_for === false && (
                          <Badge colorScheme="red" variant="outline" size="sm">
                            Makeup Required
                          </Badge>
                        )}
                        {session.madeup_for === true &&
                          isCancelledOrLateCancellationStatus(session.status) &&
                          session.makeup_session_date && (
                          <Badge colorScheme="green" variant="outline" size="sm">
                            Made up for on {new Date(session.makeup_session_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Badge>
                        )}
                      </VStack>
                    </Td>
                    <Td display={{ base: "none", lg: "table-cell" }}>
                      <Text fontSize="sm" noOfLines={2}>
                        {session.notes || '-'}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Alert status="info">
              <AlertIcon />
              <AlertDescription>
                No sessions recorded yet. Session history will appear here as your child attends therapy.
              </AlertDescription>
            </Alert>
          )}
        </CardBody>
      </Card>
    </VStack>
  );
};

const ParentDashboard = () => {
  const { profile, loading } = useAuth();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [children, setChildren] = useState([]);
  const [selectedChildIndex, setSelectedChildIndex] = useState(0);
  const [userInfo, setUserInfo] = useState(null);
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const mainContainerBG = useColorModeValue('gray.50', 'gray.900');
  const cardBG = useColorModeValue('white', 'gray.800');
  const headingBG = useColorModeValue('gray.800', 'white');
  const textBG = useColorModeValue('gray.600', 'gray.300');
  const hStackBG = useColorModeValue('gray.500', 'gray.400');

  // Get currently selected child
  const selectedChild = children[selectedChildIndex] || null;

  useEffect(() => {
    if (profile) {
      fetchChildren();
    }
  }, [profile]);

  const fetchChildren = async () => {
    try {
      // Find all clients by matching guardian_email with user's email
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('guardian_email', profile.email)
        .order('name');

      if (error) throw error;
      
      setChildren(data || []);
      // Reset selected child index if we have fewer children than before
      if (data && data.length > 0 && selectedChildIndex >= data.length) {
        setSelectedChildIndex(0);
      }
    } catch (error) {
      console.error('Error fetching children:', error);
    }
  };


  if (loading) {
    return <LoadingOverlay />;
  }


  if (!profile || profile.role !== 'parent') {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You must be logged in as a parent to view this dashboard.
          </AlertDescription>
        </Alert>
      </Container>
    );
  }

  const handleTabChange = (index) => {
    setSelectedTabIndex(index);
  };

  const navigationItems = [
    { icon: FiFileText, label: "Documents", index: 0 },
    { icon: FiCalendar, label: "Sessions", index: 1 },
    { icon: FiMessageCircle, label: "Requests", index: 2 },
  ];

  return (
    <Box minH="100vh" bg={mainContainerBG}>
      <Container maxW="7xl" py={8}>
        {/* Header */}
        <Card mb={8} bg={cardBG} shadow="lg">
          <CardBody>
            <VStack spacing={4} align="stretch">
              {/* Child Selector - only show if multiple children */}
              {children.length > 1 && (
                <HStack justify="space-between" align="center">
                  <HStack>
                    <Icon as={FiUsers} color="#24b5e8" />
                    <Text fontWeight="medium" color={headingBG} display={{ base: "none", md: "block" }}>Select Child:</Text>
                  </HStack>
                  <Menu>
                    <MenuButton as={Button} rightIcon={<FiChevronDown />} variant="outline">
                      {selectedChild?.name || 'Select a child'}
                    </MenuButton>
                    <MenuList>
                      {children.map((child, index) => (
                        <MenuItem 
                          key={child.id} 
                          onClick={() => setSelectedChildIndex(index)}
                          bg={index === selectedChildIndex ? "blue.50" : "transparent"}
                        >
                          <HStack>
                            <Avatar size="sm" name={child.name} />
                            <VStack align="start" spacing={0}>
                              <Text>{child.name}</Text>
                              {child.nickname && (
                                <Text fontSize="xs" color="gray.500">"{child.nickname}"</Text>
                              )}
                            </VStack>
                          </HStack>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </HStack>
              )}
              
              {/* Child Info */}
              <HStack spacing={6}>
                <Avatar 
                  size="lg" 
                  name={selectedChild?.name} 
                  bg="linear-gradient(135deg, #24b5e8, #abdb15)"
                  color="white"
                />
                <VStack align="start" spacing={1}>
                  <Heading size="lg" color={headingBG}>
                    {selectedChild?.name || 'Your Child'}
                    {selectedChild?.nickname && (
                      <Text as="span" fontSize="md" color="gray.500" ml={2}>
                        "{selectedChild.nickname}"
                      </Text>
                    )}
                  </Heading>
                  <Text color={textBG}>
                    {children.length > 1 ? 'Therapy portal' : 'Welcome to your therapy portal'}
                  </Text>
                  <HStack spacing={4} fontSize="sm" color={hStackBG}>
                    {getClientDisplayAge(selectedChild) !== null && (
                      <Text>Age: {getClientDisplayAge(selectedChild)}</Text>
                    )}
                    {/*selectedChild?.diagnosis && <Text>• {selectedChild.diagnosis}</Text>*/}
                  </HStack>
                </VStack>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        <Tabs 
          isFitted 
          variant="enclosed" 
          index={selectedTabIndex} 
          onChange={handleTabChange}
        >
          {/* Hide TabList on mobile */}
          <TabList mb="1em" display={{ base: "none", md: "flex" }}>
            <Tab>Documents</Tab>
            <Tab>Session Log</Tab>
            <Tab>Document Requests</Tab>
          </TabList>

          <TabPanels>
            <TabPanel position="relative" minHeight="500px">
              {selectedChild ? (
                <DocumentTrail childId={selectedChild.id} />
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <AlertDescription>
                    No children found. Please contact support if you believe this is an error.
                  </AlertDescription>
                </Alert>
              )}
            </TabPanel>
            <TabPanel position="relative" minHeight="500px">
              {selectedChild ? (
                <SessionLog childId={selectedChild.id} />
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <AlertDescription>
                    No children found. Please contact support if you believe this is an error.
                  </AlertDescription>
                </Alert>
              )}
            </TabPanel>
            <TabPanel position="relative" minHeight="500px">
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  Document request functionality coming soon! You'll be able to request progress reports, 
                  therapy certificates, and other documents directly from this portal.
                </AlertDescription>
              </Alert>
            </TabPanel>
          </TabPanels>

          {/* Bottom Navigation for Mobile */}
          <Box
            display={{ base: "block", md: "none" }}
            position="fixed"
            bottom="0"
            left="0"
            right="0"
            bg="white"
            borderTop="1px"
            borderColor="gray.200"
            boxShadow="0 -2px 10px rgba(0, 0, 0, 0.05)"
            zIndex="docked"
          >
            <Flex justify="space-around" align="center">
              {navigationItems.map((item) => (
                <BottomNavItem
                  key={item.index}
                  icon={item.icon}
                  label={item.label}
                  isSelected={selectedTabIndex === item.index}
                  onClick={() => handleTabChange(item.index)}
                />
              ))}
            </Flex>
          </Box>

          {/* Add bottom padding on mobile to account for navigation bar */}
          <Box display={{ base: "block", md: "none" }} h="80px" />
        </Tabs>
      </Container>
    </Box>
  );
};

export default ParentDashboard;